import { useState, useRef } from 'react';
import { format } from 'date-fns';
import { DatePickerModal } from '../DatePickerModal';

interface DatePickerFieldProps {
  label: React.ReactNode;
  value?: Date | undefined;
  startDate?: Date | undefined;
  endDate?: Date | undefined;
  onChange?: (date: Date | undefined) => void;
  onRangeChange?: (start: Date | undefined, end: Date | undefined) => void;
  mode?: 'single' | 'range';
  error?: boolean;
  disabled?: boolean;
  minDate?: Date | undefined;
  maxDate?: Date | undefined;
  startYear?: number;
  endYear?: number;
  errorMessage?: string | undefined;
  ariaLabel?: string;
  pickerLabel?: string;
  onOtherPickerOpen?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  compact?: boolean;
  variant?: 'default' | 'flat';
  /** Override the resting/floating label font size (px). Defaults to 14. */
  labelFontSize?: number;
  dataTestId?: string;
  placement?:
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'left-start'
    | 'left-end'
    | 'right-start'
    | 'right-end';
}

const CalendarIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z"
      stroke="#181A1B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 2V6"
      stroke="#181A1B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 2V6"
      stroke="#181A1B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 10H21"
      stroke="#181A1B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function DatePickerField({
  label,
  value,
  startDate,
  endDate,
  onChange,
  onRangeChange,
  mode = 'single',
  error = false,
  disabled = false,
  minDate,
  maxDate,
  startYear = 2001,
  endYear = new Date().getFullYear() + 10,
  errorMessage,
  ariaLabel,
  pickerLabel,
  onOtherPickerOpen,
  open: controlledOpen,
  onOpenChange,
  compact = false,
  variant = 'default',
  dataTestId,
  placement = 'bottom-start',
  labelFontSize = 14,
}: DatePickerFieldProps) {
  const isFlat = variant === 'flat';
  const [internalOpen, setInternalOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const dateRef = useRef<HTMLDivElement>(null);

  const isControlled = controlledOpen !== undefined;
  const showPicker = isControlled ? controlledOpen : internalOpen;

  const setShowPicker = (val: boolean) => {
    if (!isControlled) {
      setInternalOpen(val);
    }
    onOpenChange?.(val);
  };

  const handleTogglePicker = () => {
    if (disabled) {
      return;
    }
    onOtherPickerOpen?.();
    setShowPicker(!showPicker);
    setIsFocused(!showPicker);
  };

  return (
    <div className="flex-1 relative">
      <div className="flex flex-col gap-1">
        <div
          ref={dateRef}
          className={`relative w-full h-14 bg-[#E5E7E9] ${isFlat ? 'rounded-lg' : 'rounded'} flex items-center ${isFlat ? 'pl-4 pr-3' : 'pl-4 pr-2'} gap-2 cursor-pointer ${isFlat ? 'hover:bg-[#eceeef]' : 'hover:bg-[#D5D7D9]'} ${isFlat ? '' : 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]'} transition-colors ${
            disabled ? 'opacity-60 cursor-not-allowed hover:bg-[#E5E7E9]' : ''
          }`}
          style={{
            borderBottom: error
              ? '2px solid #E11B1B'
              : isFocused || showPicker
                ? '2px solid #2a4455'
                : 'none',
            ...(isFlat ? { outline: 'none' } : {}),
          }}
          onClick={handleTogglePicker}
          onFocus={() => !disabled && setIsFocused(true)}
          onBlur={() => {
            setTimeout(() => {
              if (!showPicker) {
                setIsFocused(false);
              }
            }, 200);
          }}
          onKeyDown={(e) => {
            if (disabled) {
              return;
            }
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleTogglePicker();
            }
          }}
          tabIndex={disabled ? -1 : 0}
          role="button"
          data-testid={dataTestId}
          aria-label={
            ariaLabel || (typeof label === 'string' ? label : 'Date picker')
          }
        >
          <div className="flex-1 flex flex-col">
            <span
              className="font-normal tracking-[0.25px] transition-all duration-200 ease-out"
              style={{
                color: isFocused || showPicker ? '#2a4455' : '#474A4C',
                fontSize: `${labelFontSize}px`,
                lineHeight: '20px',
                marginTop: '0px',
                fontWeight: 400,
              }}
            >
              {label}
            </span>
            <span
              className={`text-[#181A1B] ${isFlat ? 'text-base' : 'text-sm'} leading-5 transition-all duration-200 ease-out`}
              style={{
                marginTop: value || startDate ? '2px' : '4px',
              }}
            >
              {mode === 'range'
                ? startDate
                  ? `${format(startDate, 'yyyy/MM/dd')}${endDate ? ` - ${format(endDate, 'yyyy/MM/dd')}` : ''}`
                  : ''
                : value
                  ? format(value, 'yyyy/MM/dd')
                  : ''}
            </span>
          </div>
          {!isFocused && !showPicker && !error && (
            <div
              className="absolute bottom-0"
              style={{
                left: '6px',
                right: '6px',
                height: '1px',
                backgroundColor: isFlat ? '#6a7278' : '#474A4C',
              }}
            />
          )}
          <div className="p-2 rounded-full hover:bg-black/5 transition-colors shrink-0">
            <CalendarIcon />
          </div>
        </div>

        <DatePickerModal
          isOpen={showPicker}
          onClose={() => {
            setShowPicker(false);
            setIsFocused(false);
          }}
          onSelect={onChange || (() => {})}
          onRangeSelect={onRangeChange}
          mode={mode}
          selected={value}
          startDate={startDate}
          endDate={endDate}
          label={
            pickerLabel ??
            (typeof label === 'string' ? label : (ariaLabel ?? ''))
          }
          anchorEl={dateRef.current}
          minDate={minDate}
          maxDate={maxDate}
          startYear={startYear}
          endYear={endYear}
          placement={placement}
          compact={compact}
        />
        {error && errorMessage && (
          <span className="text-[#E11B1B] text-xs mt-1">{errorMessage}</span>
        )}
      </div>
    </div>
  );
}
